import classnames from 'classnames';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Button, Card, FormGroup } from 'react-bootstrap';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Truncate from 'react-truncate';
import avatarProvider from 'v2//images/avatarProvider.png';
import { GenericObject, ProposalEntity, SearchEntity, User } from 'v2/models';
import AvatarNameDetail from 'v2/pages/SearchDetails/SearchResponse/AvatarNameDetail';
import { ActiveRequestsTabType } from '../ActiveDataRequests/ActiveDataRequests';
import ProposalRespondModal from '../ActiveDataRequests/ProposalRespondModal/ProposalRespondModal';
import { ClosedRequestsTabType } from '../ClosedDataRequests/ClosedDataRequests';
import styles from './dataRequestItem.module.scss';
import MarkdownRender from '../../../../components/MarkdownRender/MarkdownRender';
import DiscussionButton from './DiscussionButton';
import ShowMoreRequestItem from './ShowMoreRequestItem/ShowMoreRequestItem';
import { WaitingSellerActions } from './WaitingSellerActions';

interface StateProps {
  user: User;
}
export interface DataRequestItemOwnProps {
  tabKey: ActiveRequestsTabType['eventKey'] | ClosedRequestsTabType['eventKey'];
  proposal: ProposalEntity;
  className?: string;
}

type DataRequestItemProps = StateProps & DataRequestItemOwnProps;

const DataRequestItem = ({
  proposal,
  className,
  tabKey,
  user,
}: DataRequestItemProps) => {
  const location = useLocation();
  const { hash } = location;
  const [showMoreDescription, setShowMoreDescription] = useState(false);
  const [searchDescrTruncated, setSearchDescrTruncated] = useState(false);
  const hashRef = useRef<HTMLSpanElement>(null);
  const [showProposalResponseModal, setShowProposalResponseModal] =
    useState(false);
  const isAnonymous = !proposal.data_buyer.name;
  const search = proposal.search as SearchEntity;

  const handleTruncateSearchDescr = (_truncated: boolean) => {
    if (_truncated && _truncated !== searchDescrTruncated) {
      setSearchDescrTruncated(_truncated);
    }
  };

  useLayoutEffect(() => {
    if (hash) {
      const idFromHash = hash.slice(1);
      if (Number(idFromHash) === proposal.id && hashRef.current) {
        const topNavHeight = 100;
        const paddingTop = 27;
        window.scrollTo(
          0,
          (hashRef.current?.getBoundingClientRect().top || 0) +
            document.documentElement.scrollTop -
            (topNavHeight + paddingTop)
        );
      }
    }
  }, [hash, proposal.id]);

  const renderDescription = () => {
    return (
      <>
        <Truncate
          lines={!showMoreDescription ? 4 : 0}
          onTruncate={handleTruncateSearchDescr}
        >
          {isAnonymous
            ? proposal.data_buyer.vague_description
            : proposal.data_buyer.short_description ||
              'No Description Available'}
        </Truncate>
        {searchDescrTruncated ? (
          <Button
            className={classnames('p-0', { invisible: !searchDescrTruncated })}
            variant="link"
            onClick={() => setShowMoreDescription(!showMoreDescription)}
            disabled={!searchDescrTruncated}
            aria-label="showMoreDescriptionBtn"
          >
            {showMoreDescription ? 'Show Less' : 'Show More'}
          </Button>
        ) : null}
      </>
    );
  };

  const renderAvatar = () => {
    const buyerOrganization = proposal.data_buyer;
    const imageUrl = buyerOrganization.avatar?.image?.formats?.thumbnail.url;
    return (
      <AvatarNameDetail
        image={imageUrl || avatarProvider}
        title={proposal.data_buyer.name || 'Anonymous'}
        subtitle={buyerOrganization.classification}
      />
    );
  };

  const renderWaitingSellerActions = () => {
    return tabKey === 'waiting-seller' ? (
      <WaitingSellerActions
        proposal={proposal}
        onRespondProposal={(show = true) => setShowProposalResponseModal(show)}
        user={user}
      />
    ) : null;
  };

  const renderProposalResponseModal = () => {
    if (tabKey === 'waiting-seller' && showProposalResponseModal) {
      return (
        <ProposalRespondModal
          key={String(showProposalResponseModal)}
          proposal={proposal}
          show={showProposalResponseModal}
          onHide={() => setShowProposalResponseModal(false)}
        />
      );
    }
    return null;
  };

  return (
    <div
      className={classnames('rounded border-light5', styles.root, className)}
      data-cy="proposal-item-info"
    >
      <Card className={classnames('rounded-left border-light5')}>
        <Card.Body className={classnames('p-0 rounded')}>
          <div className="p-4 d-flex flex-column justify-content-between h-100">
            <div>
              <FormGroup>
                <span id={`${proposal.id}`} ref={hashRef}></span>
                <label className="text-primary-dark">{search.name}</label>
                <MarkdownRender
                  className="markdown-text-blue-light pr-4"
                  source={search.description}
                  format={true}
                  preserveSpaces
                />
              </FormGroup>
              <ShowMoreRequestItem proposal={proposal} />
            </div>
            {renderWaitingSellerActions()}
            {tabKey === 'approved' && <DiscussionButton proposal={proposal} />}
          </div>
        </Card.Body>
      </Card>
      <div
        className={classnames(
          'd-none d-lg-block',
          'bg-light',
          'p-4',
          'border border-light5 border-left-0',
          'rounded-right'
        )}
      >
        {renderAvatar()}
        <p className="text-light4 pt-3">{renderDescription()}</p>
      </div>
      {renderProposalResponseModal()}
    </div>
  );
};

const mapStateToProps = (state: GenericObject): StateProps => {
  return {
    user: state.user.user,
  };
};

export default connect<StateProps, DataRequestItemOwnProps>(mapStateToProps)(
  DataRequestItem
);
